import { observer } from "mobx-react-lite";
import { useRef } from "react";

import { VariableSet } from "../../../Playground/types";
import { useConfiguratorStore } from "../../input-variable-configurator-store-context";

import OpenLibraryButton from "./OpenLibraryButton";
import SaveInputVariableSetButton from "./SaveInputVariableSetButton";
import { useFindShortcutMask } from "./hooks/useFindShortcutMask";

import SearchBar from "@/components/ui/SearchBar";
import { RequestToolData } from "@/queries/requests/useRequestLogInputVariables";

interface ConfiguratorHeaderProps {
  hideImportButton?: boolean;
  hideExportButton?: boolean;
  enableAIDataset?: boolean;
  promptTemplate?: string;
  onSetNameImported?: (name: string) => void;
  onRequestImported?: (
    requestId: number,
    variables: Record<string, string>,
    toolData?: RequestToolData | null,
  ) => void;
  normalizeImportedVariables?: (variables: Record<string, string>) => Record<string, string>;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  showSearch?: boolean;
  activeSetName?: string;
  onAddVariableSet?: (variableSet: VariableSet) => void;
  currentVariableSetCount?: number;
}

const ConfiguratorHeader = observer(
  ({
    hideImportButton = false,
    hideExportButton = false,
    enableAIDataset = false,
    promptTemplate = "",
    onSetNameImported,
    onRequestImported,
    normalizeImportedVariables,
    searchQuery = "",
    onSearchChange,
    showSearch = false,
    activeSetName,
    onAddVariableSet,
    currentVariableSetCount = 0,
  }: ConfiguratorHeaderProps) => {
    const store = useConfiguratorStore();
    const searchInputRef = useRef<HTMLInputElement>(null);

    useFindShortcutMask({
      searchInputRef,
      enabled: showSearch,
    });

    const handleVariablesFetched = (variables: Record<string, string>, setName?: string) => {
      store.importVariables(variables);
      if (setName) {
        store.setName(setName);
        onSetNameImported?.(setName);
      }
    };

    return (
      <div className="flex w-full flex-col gap-2 border-b border-gray-100 px-1 py-2">
        <div className="flex w-full items-center gap-2">
          {!hideImportButton && (
            <OpenLibraryButton
              activeSetName={activeSetName}
              onVariablesFetched={handleVariablesFetched}
              onRequestImported={onRequestImported}
              normalizeImportedVariables={normalizeImportedVariables}
              enableAIDataset={enableAIDataset}
              promptTemplate={promptTemplate}
              onAddVariableSet={onAddVariableSet}
              currentVariableSetCount={currentVariableSetCount}
              variableNames={store.inputVariables.map(variable => variable.key)}
            />
          )}

          {showSearch && onSearchChange && (
            <div className="min-w-[180px] flex-1">
              <SearchBar
                ref={searchInputRef}
                placeholderPhrasesArray={["Search variables..."]}
                value={searchQuery}
                onChange={onSearchChange}
                size="xs"
              />
            </div>
          )}

          <div className="ml-auto flex items-center gap-1">
            {!hideExportButton && <SaveInputVariableSetButton variables={store.inputVariables} />}
          </div>
        </div>
        {activeSetName && (
          <p className="px-1 text-xs text-gray-500">
            Imports add or update values in{" "}
            <span className="font-medium text-gray-700">{activeSetName}</span>.
          </p>
        )}
      </div>
    );
  },
);

ConfiguratorHeader.displayName = "ConfiguratorHeader";

export default ConfiguratorHeader;
